import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { useAuth } from "../lib/hooks/useAuth";
import { microsoftAuth } from "../lib/services/microsoftAuth";
import { useAuthStore } from "@/lib";
import { useViewStore } from "@/lib/stores/view";

interface MicrosoftCallbackProps {
	onSuccess: () => void;
	onError: () => void;
	onAccessDenied: () => void;
}

export default function MicrosoftCallback({ onSuccess, onError, onAccessDenied }: MicrosoftCallbackProps) {
	const { loginWithMicrosoft } = useAuth();
	const { unAuthorized } = useAuthStore();
	const [status, setStatus] = useState<"processing" | "success" | "error" | "access-denied">("processing");
	const [message, setMessage] = useState("Đang xử lý đăng nhập Microsoft...");
	const isProcessedRef = useRef(false);
	const { setCurrentView } = useViewStore();

	const STATE_KEY = "microsoft_oauth_state";

	// Effect 1: xử lý callback chỉ chạy 1 lần
	useEffect(() => {
		const handleCallback = async () => {
			if (isProcessedRef.current) return;
			isProcessedRef.current = true;

			try {
				const urlParams = new URLSearchParams(window.location.search);
				const stateFromUrl = urlParams.get("state");
				const storedState = sessionStorage.getItem(STATE_KEY);
				const codeVerifier = localStorage.getItem("code_verifier");

				if (stateFromUrl !== storedState) {
					setStatus("error");
					setMessage("State không hợp lệ, có thể là tấn công CSRF");
					return;
				}

				const code = urlParams.get("code");
				if (!code) {
					setStatus("error");
					setMessage("Không nhận được mã xác thực từ Microsoft");
					return;
				}

				setStatus("processing");
				setMessage("Đang xử lý đăng nhập...");

				const result = await loginWithMicrosoft(code, stateFromUrl ?? undefined, codeVerifier ?? undefined);

				if (result?.success) {
					if (unAuthorized()) {
						setStatus("access-denied");
						setMessage("Bạn không có quyền truy cập");
					} else {
						setStatus("success");
						setMessage("Đăng nhập Microsoft thành công!");
						setCurrentView("admin");
					}
				} else {
					setStatus("error");
					setMessage(result?.error || "Đăng nhập Microsoft thất bại");
				}
			} catch (error) {
				console.error("Error handling Microsoft callback:", error);
				setStatus("error");
				setMessage("Có lỗi xảy ra khi xử lý đăng nhập Microsoft");
			} finally {
				// Clear query params
				window.history.replaceState({}, document.title, window.location.pathname);
			}
		};

		handleCallback();
	}, [loginWithMicrosoft, unAuthorized]);

	// Effect 2: redirect khi status thay đổi
	useEffect(() => {
		if (status === "success") {
			const timer = setTimeout(() => onSuccess(), 1500);
			return () => clearTimeout(timer);
		}
		else if (status === "error") {
			const timer = setTimeout(() => onError(), 1500);
			return () => clearTimeout(timer);
		}
		else if (status === "access-denied") {
			const timer = setTimeout(() => onAccessDenied(), 1500);
			return () => clearTimeout(timer);
		}
	}, [status, onSuccess, onError]);

	const handleRetry = () => {
		microsoftAuth.clearCallbackParams();
		onError();
	};

	return (
		<div className='min-h-screen flex items-center justify-center bg-muted/50 p-4'>
			<Card className='w-full max-w-md'>
				<CardHeader className='text-center'>
					<div className='flex justify-center mb-4'>
						{status === "processing" && <Loader2 className='w-8 h-8 text-primary animate-spin' />}
						{status === "success" && <CheckCircle className='w-8 h-8 text-green-500' />}
						{status === "error" && <XCircle className='w-8 h-8 text-destructive' />}
					</div>
					<CardTitle className='text-xl'>
						{status === "processing" && "Đang xử lý..."}
						{status === "success" && "Thành công!"}
						{status === "error" && "Có lỗi xảy ra"}
						{status === "access-denied" && "Bạn không có quyền truy cập"}
					</CardTitle>
				</CardHeader>
				<CardContent className='text-center space-y-4'>
					<Alert variant={status === "error" ? "destructive" : "default"}>
						<AlertDescription className='text-center'>{message}</AlertDescription>
					</Alert>

					{status === "success" && <p className='text-sm text-muted-foreground'>Đang chuyển hướng...</p>}

					{/* {status === "error" && (
						<div className='space-y-2'>
							<Button onClick={handleRetry} className='w-full'>
								Quay lại trang đăng nhập
							</Button>
							<p className='text-xs text-muted-foreground'>Nếu lỗi vẫn tiếp tục, vui lòng liên hệ quản trị viên</p>
						</div>
					)} */}
				</CardContent>
			</Card>
		</div>
	);
}
